import jsPDF from 'jspdf';

import type { ParticipantTableItem } from '../types/previdencia/table/participantTableType';

/**
 * Sanitizes a string for use in filename
 */
const sanitizeFileName = (str: string): string => {
  return str.replace(/[^a-zA-Z0-9]/g, '_');
};

/**
 * Helper function to add text with word wrap to PDF document
 */
const addTextToPdf = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number = 12,
  isBold: boolean = false
): number => {
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', isBold ? 'bold' : 'normal');
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + (lines.length * fontSize * 0.4);
};

/**
 * Creates a header section in PDF with title
 */
const addPdfHeader = (
  doc: jsPDF,
  title: string,
  yPosition: number,
  pageWidth: number,
  margin: number
): number => {
  doc.setFillColor(220, 38, 38); // #dc2626
  doc.rect(margin, yPosition, pageWidth - (margin * 2), 30, 'F');
  doc.setTextColor(255, 255, 255);
  const newY = addTextToPdf(doc, title, margin + 10, yPosition + 20, pageWidth - (margin * 2), 24, true);
  doc.setTextColor(0, 0, 0);
  return newY + 10;
};

/**
 * Adds a label-value pair to PDF in two-column layout
 */
const addLabelValuePair = (
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  columnWidth: number
): number => {
  doc.setFontSize(10);
  doc.setTextColor(102, 102, 102);
  let newY = addTextToPdf(doc, label, x, y, columnWidth, 10, true);
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  newY = addTextToPdf(doc, value, x, newY + 5, columnWidth, 12);
  return newY + 10;
};

/**
 * Generates a PDF document for a participant and downloads it automatically
 * @param participant - Participant data to export
 * @param formatCpf - Optional function to format CPF
 */
export const generateParticipantPDF = (
  participant: ParticipantTableItem,
  formatCpf?: (cpf: string) => string
): void => {
  const sanitizedName = sanitizeFileName(participant.participantName || 'Participante');
  const fileName = `Participante_${sanitizedName}.pdf`;

  // Create new PDF document
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPosition = margin;

  // Header
  yPosition = addPdfHeader(doc, 'DADOS DO PARTICIPANTE', yPosition, pageWidth, margin);

  // Participant information
  const infoStartY = yPosition;
  const leftColumnX = margin;
  const rightColumnX = pageWidth / 2 + 10;
  const columnWidth = (pageWidth - margin * 2) / 2 - 10;

  // Left column
  yPosition = infoStartY;
  yPosition = addLabelValuePair(doc, 'NOME DO PARTICIPANTE', participant.participantName || '-', leftColumnX, yPosition, columnWidth);
  yPosition = addLabelValuePair(doc, 'CPF', formatCpf ? formatCpf(participant.cpf) : participant.cpf || '-', leftColumnX, yPosition, columnWidth);
  yPosition = addLabelValuePair(doc, 'NÚMERO DE MATRÍCULA', participant.registrationNumber || '-', leftColumnX, yPosition, columnWidth);

  // Right column
  addLabelValuePair(doc, 'NÚMERO DO CONTRATO', participant.contractNumber || '-', rightColumnX, infoStartY, columnWidth);
  addLabelValuePair(doc, 'EMPRESA', participant.companyName || '-', rightColumnX, infoStartY + 30, columnWidth);

  // Save PDF
  doc.save(fileName);
};

